import { SkeletonLoader } from "@/components"
import { ExternalLink as ExternalLinkIcon } from "lucide-react"
import { useFetchIntegrations } from "@/hooks/queries/useFetchIntegrations"
import { useTranslation } from "react-i18next"
import { errorHandler } from "@/services/api/helpers"
import { useEffect } from "react"
import { routes } from "@/lib/routes.ts"
import { useNavigate, useSearchParams } from "react-router-dom"
import { IntegrationCard } from "@/pages/settings/integrations/jira/IntegrationCard.tsx"
import { JiraIntegration } from "@/models/Integration.ts"
import { useFetchOAuthState } from "@/hooks/queries/useFetchOAuthState.tsx"
import { runtimeConfig } from "@/lib/config.ts"

import { Link } from "react-router-dom"
import { Button } from "@/components"

const JiraConfigureButton = () => {
  const { t } = useTranslation("Integrations")
  const { data: oauthState } = useFetchOAuthState("jira")

  const jiraIntegrationUrl = `https://auth.atlassian.com/authorize?client_id=${runtimeConfig.VITE_JIRA_CLIENT_ID}&state=${oauthState?.state}&scope=read:jira-work%20offline_access&audience=api.atlassian.com&response_type=code&prompt=consent`

  return (
    <Link to={jiraIntegrationUrl} target="_blank" rel="noopener noreferrer">
      <Button variant="secondary" size="sm" className="gap-1 text-xs">
        <ExternalLinkIcon size={14} /> {t("Configure")}
      </Button>
    </Link>
  )
}

export const Jira = () => {
  const { t } = useTranslation("Integrations")
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { error, isLoading, data } = useFetchIntegrations("jira")
  document.title = "Settings - Jira Integrations"

  const code = searchParams.get("code")
  const state = searchParams.get("state")

  useEffect(() => {
    if (code && state) {
      navigate({
        pathname: routes.settings.jiraCallback,
        search: `?state=${state}&code=${code}`
      })
    }
  }, [code, navigate, state])

  if (isLoading) {
    return (
      <div>
        <SkeletonLoader />
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <p>{errorHandler(error)}</p>
      </div>
    )
  }

  if (data && data.integrations?.length > 0) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h1 className="text-sm font-medium text-secondary">{t("Jira")}</h1>
          <JiraConfigureButton />
        </div>
        <div className="grid w-full grid-cols-2 gap-4">
          {data.integrations.map((integration) => (
            <IntegrationCard key={integration.id} integration={integration as JiraIntegration} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div data-testid="jira-integration-description" className="text-primary">
      <p className="mb-4">{t("jira_description.first")}</p>
      <p className="mb-4">{t("jira_description.second")}</p>
      <JiraConfigureButton />
    </div>
  )
}
